import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
	EditQuizStep,
	getEditQuizStepName,
	isProctoringOptionCategory,
} from "../proctoring-options/option-text/ProctoringOptionText";
import { StepConfig } from "../../core/stepper/Stepper";
import { EditQuizPageProps } from "../editQuiz";
import { UseEditQuizStateReturn } from "./state/useEditQuizState";
import {
	checkIfPortalThirdPartyAssessment,
	checkIfStandaloneAssessment,
	checkIfVerityAssessment,
	getAvailableProctoringMethods,
	getProctoringMethod,
	isThirdPartyStepValid,
} from "../editQuizHelper";
import { getEditQuizGuideState } from "../../setup-guide/QuickSetupText";

type UseEditQuizNavigationProps = Pick<
	EditQuizPageProps,
	| "proctoringAssessmentConfiguration"
	| "enforcedProfilesOnly"
	| "setProctoringAssessmentConfiguration"
	| "onBackEnabledChange"
	| "onNextEnabledChange"
	| "setIsFinalPage"
	| "quizType"
	| "setIsFirstPage"
	| "entryPoint"
	| "proctoringOption"
	| "quizInfo"
	| "setProctoringOption"
> &
	Pick<UseEditQuizStateReturn, "nav" | "live" | "setting" | "profile" | "student">;

/**
 * Handles stepper, next, back buttons.
 * Originally in editQuiz.tsx
 */
export default function useEditQuizNavigation({
	proctoringAssessmentConfiguration,
	enforcedProfilesOnly,
	setProctoringAssessmentConfiguration,
	onBackEnabledChange,
	onNextEnabledChange,
	setIsFinalPage,
	quizType,
	setIsFirstPage,
	entryPoint,
	proctoringOption,
	quizInfo,
	setProctoringOption,
	...props
}: UseEditQuizNavigationProps) {
	const {
		verityInstituteSettings,
		allowedCategories,
		enabledProctoringOptions,
		proctoringData,
		loading,
		isSetupGuideWorkflow,
	} = props.setting.state;
	const { validateReferenceMaterialSetting } = props.setting.action;
	const { validateLiveSession, resetConfirmationMessage } = props.live.action;
	const {
		focusStep,
		handleInitialNavigation,
		resetStickyPage,
		shouldStartOnThirdPartyStep,
		markMultipleStepsCompleted,
		markStepAsCompleted,
	} = props.nav.action;
	const { setCurPageValue } = props.nav.internal;
	const { curPageValue, shouldStartOnLastStep, stickyPageName, completedStepsSet } = props.nav.state;
	const { activeProfile, canShowProfileStep, profiles } = props.profile.state;
	const { accommodationView } = props.student.state;

	// whenever step changes, focus the modal body
	useEffect(() => {
		focusStep();
	}, [curPageValue, focusStep]);

	/**Undefined until institute settings load, since availability depends on them */
	const availableProctoringMethods = useMemo(
		() =>
			verityInstituteSettings
				? getAvailableProctoringMethods(enabledProctoringOptions, verityInstituteSettings)
				: undefined,
		[enabledProctoringOptions, verityInstituteSettings],
	);

	const singleAvailableMethod = availableProctoringMethods?.length === 1 ? availableProctoringMethods[0] : undefined;
	const currentProctoringMethod = getProctoringMethod(proctoringAssessmentConfiguration);
	const hasResolvedInitialStep = useRef(false);

	const fixedPageNames = useMemo(() => {
		const pageNames: EditQuizStep[] = [];

		const isVerityAssessment = checkIfVerityAssessment(quizType);
		const isStandalone = checkIfStandaloneAssessment(quizType);
		const isPortalThirdParty = checkIfPortalThirdPartyAssessment(quizType, entryPoint);

		// Verity Assessment is technically a third party, but we don't want users to configure it
		if (proctoringAssessmentConfiguration?.thirdPartyAssessmentOption && !isVerityAssessment && !isPortalThirdParty) {
			pageNames.push("thirdParty");
		}
		if (!verityInstituteSettings?.liteMode) {
			pageNames.push("method");
		}

		if (canShowProfileStep) {
			pageNames.push("profile");
		}

		//  These quiz types renders their own review step
		const rendersSeparateReviewStep =
			(isVerityAssessment || isStandalone || isPortalThirdParty) && entryPoint === "lti-page";

		if (!rendersSeparateReviewStep) {
			pageNames.push("confirmation");
		}

		return pageNames;
	}, [
		canShowProfileStep,
		entryPoint,
		proctoringAssessmentConfiguration?.thirdPartyAssessmentOption,
		quizType,
		verityInstituteSettings?.liteMode,
	]);

	const getLiveSessionsStepIndex = useCallback(() => {
		return fixedPageNames.indexOf("method") + 1;
	}, [fixedPageNames]);

	const totalPageNames = useMemo(() => {
		const pages = [...fixedPageNames];
		if (proctoringAssessmentConfiguration?.isLiveProctored) {
			pages.splice(pages.indexOf("method") + 1, 0, "liveSessions");
		}
		if (!enforcedProfilesOnly) {
			const configurationPages: EditQuizStep[] = ["platform"];
			if (allowedCategories.length > 0) configurationPages.push("configuration", ...allowedCategories);
			const lastSetupPage = pages.findLastIndex((page) => page !== "confirmation");
			pages.splice(lastSetupPage + 1, 0, ...configurationPages);
		}
		return pages;
	}, [fixedPageNames, proctoringAssessmentConfiguration?.isLiveProctored, enforcedProfilesOnly, allowedCategories]);

	const stepConfig = useMemo(() => {
		const config: StepConfig<EditQuizStep>[] = [];

		totalPageNames.forEach((pageName) => {
			config.push({
				key: pageName,
				label: getEditQuizStepName(pageName),
				// If it's a category, its parent is "Configuration"
				...(isProctoringOptionCategory(pageName) && { parentSubstep: "configuration" as EditQuizStep }),
			});
		});

		return config;
	}, [totalPageNames]);

	const curPageName: EditQuizStep = useMemo(() => totalPageNames[curPageValue], [curPageValue, totalPageNames]);
	const totalPageIndex = useMemo(() => totalPageNames.length - 1, [totalPageNames.length]);

	// IP address restriction is meaningless with no ranges configured, so auto-disable it once the user
	// reaches the review step rather than letting them save a restriction that blocks nobody.
	useEffect(() => {
		if (
			curPageName === "confirmation" &&
			proctoringOption?.enableIpAddressRestriction &&
			!proctoringOption.allowedIpAddresses?.length
		) {
			setProctoringOption({
				...proctoringOption,
				enableIpAddressRestriction: false,
				allowedIpAddresses: undefined,
			});
		}
	}, [curPageName, proctoringOption, setProctoringOption]);

	/**Sets all steps up to the targetIndex as completed */
	const setAllPrevStepCompleted = useCallback(
		(targetIndex: number) => {
			// We take everything from index 0 up to the target index
			const skippedPages = totalPageNames.slice(0, targetIndex);
			markMultipleStepsCompleted(skippedPages);
		},
		[markMultipleStepsCompleted, totalPageNames],
	);

	/**
	 * Pass in the step to go to. This function will update necessary state, and do validation checks.
	 *
	 * Pass silentValidation=true when the jump is expected to skip over steps the user never looked at
	 * (e.g. applying a profile jumps straight from "profile" to the last step). Steps that fail
	 * validation are still landed on, but without a toast, since the user never tried to leave them.
	 */
	const navigateToStep = useCallback(
		(targetStep: number, { silentValidation = false }: { silentValidation?: boolean } = {}) => {
			resetStickyPage();

			// Deliberately not a `setCurPageValue` updater: React runs updaters during the render phase, so
			// the validation toasts and `setAllPrevStepCompleted` below would fire mid-render — which React
			// reports as "Cannot update a component while rendering a different component", `curPageValue`
			// being the outer wizard's state and the completed-step set being this stepper's.
			const prevIndex = curPageValue;
			const hasCategories = allowedCategories.length > 0;

			const isMovingForward = targetStep > prevIndex;
			const targetPageName = totalPageNames[targetStep];

			let finalTarget = targetStep;

			// Handle the Configuration skip
			if (targetPageName === "configuration" && hasCategories) {
				finalTarget = isMovingForward ? targetStep + 1 : targetStep - 1;
			}

			const safeTarget = Math.max(0, Math.min(totalPageIndex, finalTarget));

			// Logic check: if we aren't actually moving, do nothing
			if (safeTarget === prevIndex) return;

			// Validation (Forward only). Check every step being passed through, not just the one we're leaving,
			// since jumps (e.g. after applying a profile) can skip over several steps at once.
			if (isMovingForward) {
				const showToast = !silentValidation;
				for (let i = prevIndex; i < safeTarget; i++) {
					const pageName = totalPageNames[i];
					if (pageName === "liveSessions" && !validateLiveSession({ showToast })) {
						setCurPageValue(i); // Land on the failing step
						return;
					}

					if (pageName === "showTools" && !validateReferenceMaterialSetting({ showToast })) {
						setCurPageValue(i); // Land on the failing step
						return;
					}
				}

				setAllPrevStepCompleted(targetStep);
			}

			setCurPageValue(safeTarget);
		},
		[
			resetStickyPage,
			setCurPageValue,
			allowedCategories.length,
			curPageValue,
			totalPageNames,
			totalPageIndex,
			validateLiveSession,
			validateReferenceMaterialSetting,
			setAllPrevStepCompleted,
		],
	);

	const handleBackButton = useCallback(() => {
		navigateToStep(curPageValue - 1);
	}, [curPageValue, navigateToStep]);

	const handleGoToStep = useCallback(
		(pageName: EditQuizStep) => {
			const index = totalPageNames.indexOf(pageName);
			if (index >= 0) {
				navigateToStep(index);
			}
		},
		[navigateToStep, totalPageNames],
	);

	const handleNextButton = useCallback(() => {
		// Handle navigation after user selects their profile
		if (curPageName === "profile" && activeProfile) {
			// Try to go to confirmation step. Otherwise last possible step. This jumps over steps (e.g.
			// Resources) the user never saw, so validate silently rather than toasting for a page they
			// never tried to leave.
			const confirmationIndex = totalPageNames.indexOf("confirmation");
			navigateToStep(Math.max(totalPageIndex, confirmationIndex), { silentValidation: true });
			return;
		}
		navigateToStep(curPageValue + 1);
	}, [activeProfile, curPageName, curPageValue, navigateToStep, totalPageIndex, totalPageNames]);

	const getStepKeyByIndex = useCallback((index: number) => totalPageNames[index], [totalPageNames]);

	/** For callers that leave a stepper's last page by some means other than navigateToStep (e.g. an outer wizard's own Next button), so that page still gets marked completed. */
	const markCurrentStepCompleted = useCallback(() => {
		markStepAsCompleted(curPageName);
	}, [markStepAsCompleted, curPageName]);

	const handleConfirmLiveWarning = useCallback(() => {
		resetConfirmationMessage();
		if (curPageName === "liveSessions") {
			handleNextButton();
		}
	}, [curPageName, handleNextButton, resetConfirmationMessage]);

	const completedStepIndexes = useMemo(() => {
		return stepConfig
			.map((step, i) => (completedStepsSet.has(step.key) ? i : null))
			.filter((i): i is number => i !== null);
	}, [completedStepsSet, stepConfig]);

	/**Default next button enabled state*/
	const nonGuideNextButtonEnabled = useMemo(() => {
		const thirdPartyOkay = isThirdPartyStepValid(proctoringData.thirdPartyPlatforms, proctoringAssessmentConfiguration);
		return thirdPartyOkay && curPageValue < totalPageIndex;
	}, [curPageValue, proctoringAssessmentConfiguration, proctoringData.thirdPartyPlatforms, totalPageIndex]);

	const setupGuideState = useMemo(() => {
		if (
			!isSetupGuideWorkflow ||
			!verityInstituteSettings ||
			!proctoringAssessmentConfiguration ||
			!proctoringOption ||
			!quizInfo.lms
		) {
			return null;
		}

		const baseState = getEditQuizGuideState({
			curStep: curPageName,
			enabledProctoringOptions,
			verityInstituteSettings,
			proctoringAssessmentConfiguration,
			enforcedProfilesOnly,
			profiles,
			activeProfile,
			proctoringOption,
		});

		let finalTarget = baseState.highlightTarget;

		// If not specific isNextEnabled, fallback to default next button logic.
		const wouldNextBeEnabled =
			baseState.isNextEnabled !== undefined ? baseState.isNextEnabled : nonGuideNextButtonEnabled;

		if (finalTarget === "next" && !wouldNextBeEnabled) {
			finalTarget = "fallback"; // Tell step to highlight
		}

		return {
			...baseState,
			highlightTarget: finalTarget,
		};
	}, [
		isSetupGuideWorkflow,
		verityInstituteSettings,
		proctoringAssessmentConfiguration,
		proctoringOption,
		quizInfo.lms,
		curPageName,
		enabledProctoringOptions,
		enforcedProfilesOnly,
		profiles,
		activeProfile,
		nonGuideNextButtonEnabled,
	]);

	// Normalization: Handles proctoring method validation and forces user to live step is something went wrong
	useEffect(() => {
		if (availableProctoringMethods?.length && !availableProctoringMethods.includes(currentProctoringMethod)) {
			const [allowedProctoringMethod] = availableProctoringMethods;
			switch (allowedProctoringMethod) {
				case "automated":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: false,
						enablePostReview: false,
					});
					break;
				case "live": {
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: true,
						isLivePopIn: false,
						enablePostReview: false,
					});
					const liveSessionsStepIndex = getLiveSessionsStepIndex();
					if (curPageValue > liveSessionsStepIndex) {
						navigateToStep(liveSessionsStepIndex);
					}
					break;
				}
				case "pop-in":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: true,
						enablePostReview: false,
					});
					break;
				case "pop-in-post-review":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: true,
						enablePostReview: true,
					});
					break;
			}
		}
	}, [
		proctoringAssessmentConfiguration,
		currentProctoringMethod,
		availableProctoringMethods,
		setProctoringAssessmentConfiguration,
		getLiveSessionsStepIndex,
		curPageValue,
		navigateToStep,
	]);

	// Force user to be on sticky page.
	//
	// A layout effect, not a passive one — deliberately, and this is the fix for a race where an early
	// Back/Next click (before the modal has fully loaded) could skip a step. A passive effect runs after
	// the browser has already painted, so between totalPageNames reshaping (a category finishing
	// loading) and this correction landing, there was a real, paintable, clickable frame where
	// curPageValue still pointed at the OLD index — and Back/Next, reading it directly, could compute a
	// target relative to a page the screen no longer showed the same way. A layout effect runs
	// synchronously after the DOM update but BEFORE THE PAINT, and React flushes the re-render its state
	// update causes before painting too — that may be an extra render and commit, but the browser never
	// paints between them, so the stale index is never on screen for anyone (human or script) to click
	// against. This needs no memory of what was previously shown, because nothing incorrect is ever
	// shown in the first place.
	useLayoutEffect(() => {
		if (stickyPageName) {
			const newIndex = totalPageNames.indexOf(stickyPageName);

			if (newIndex !== -1) {
				// 1. Update the current page index
				if (newIndex !== curPageValue) {
					setCurPageValue(newIndex);
				}

				// 2. Sync the Completed Set
				setAllPrevStepCompleted(newIndex);
			}
		}
	}, [totalPageNames, stickyPageName, curPageValue, setCurPageValue, setAllPrevStepCompleted]);

	// Initial navigation
	useEffect(() => {
		if (loading || totalPageNames.length < 1) return;

		// Initial navigation is settled once the user is off the first step, however they got there
		if (curPageValue > 0) {
			hasResolvedInitialStep.current = true;
			return;
		}

		const startOnThirdParty = shouldStartOnThirdPartyStep(proctoringData.thirdPartyPlatforms);
		const lastPage = totalPageNames[totalPageNames.length - 1];

		if (shouldStartOnLastStep || startOnThirdParty) {
			// Third party gets higher priority
			handleInitialNavigation(startOnThirdParty ? "thirdParty" : lastPage);
			return;
		}

		// A pending page that exists in this flow is about to move the user, so let it land instead
		if (stickyPageName && totalPageNames.includes(stickyPageName)) return;

		// Check ensures that:
		// 1. if only available method IS "live", normalization (line 370) has run and liveSessions page has been ADDED if it didn't already, so we will skip to that
		// 2. if only available method IS NOT "live", normalization has run and liveSessions page has been REMOVED if it existed, so we will skip to whichever the next page is
		// 3. the configuration has loaded, since an unloaded one reads as "automated" and would skip before the start-on-last-step decision is known
		const singleMethodIsApplied =
			Boolean(proctoringAssessmentConfiguration) && singleAvailableMethod === currentProctoringMethod;

		if (curPageName === "method" && singleMethodIsApplied && !hasResolvedInitialStep.current) {
			hasResolvedInitialStep.current = true;
			navigateToStep(curPageValue + 1);
		}
	}, [
		handleInitialNavigation,
		shouldStartOnLastStep,
		totalPageNames,
		loading,
		curPageValue,
		shouldStartOnThirdPartyStep,
		proctoringData.thirdPartyPlatforms,
		singleAvailableMethod,
		curPageName,
		currentProctoringMethod,
		proctoringAssessmentConfiguration,
		stickyPageName,
		navigateToStep,
	]);

	// Calls callback functions related to enabling/disabling back/next buttons
	useEffect(() => {
		const hideBackButton = Boolean(accommodationView);

		const guideNextButtonEnabledOverride =
			setupGuideState && setupGuideState.isNextEnabled !== undefined ? setupGuideState.isNextEnabled : undefined;

		onBackEnabledChange?.({
			enabled: curPageValue > 0 && !loading && guideNextButtonEnabledOverride !== false,
			hide: hideBackButton,
		});
		setIsFirstPage?.(curPageValue === 0);
		setIsFinalPage?.(curPageValue === totalPageIndex);

		let shouldEnableNextButton =
			guideNextButtonEnabledOverride !== undefined ? guideNextButtonEnabledOverride : nonGuideNextButtonEnabled;

		if (loading) {
			shouldEnableNextButton = false;
		}

		onNextEnabledChange?.({
			enabled: shouldEnableNextButton,
			hide: Boolean(accommodationView),
			highlightText:
				setupGuideState && setupGuideState.highlightTarget === "next" && !loading
					? setupGuideState.description
					: undefined,
		});
	}, [
		curPageValue,
		onBackEnabledChange,
		setIsFinalPage,
		onNextEnabledChange,
		totalPageIndex,
		proctoringData.thirdPartyPlatforms,
		proctoringAssessmentConfiguration,
		loading,
		setIsFirstPage,
		accommodationView,
		setupGuideState,
		nonGuideNextButtonEnabled,
	]);

	return {
		curPageName,
		getStepKeyByIndex,
		stepConfig,
		handleBackButton,
		handleGoToStep,
		handleNextButton,
		handleStepChange: navigateToStep,
		completedStepIndexes,
		handleConfirmLiveWarning,
		setupGuideState,
		markCurrentStepCompleted,
	};
}

export type UseEditQuizNavigationReturn = ReturnType<typeof useEditQuizNavigation>;
